/**
 * Goggles Crashes Service
 *
 * Service layer for Goggles Crashes API calls.
 */
import { getIdToken } from '../contexts/AuthContext';
import { buildApiUrl, getAuthHeaders } from '../config/api';

/**
 * Fetches all hardware variants that have crashes.
 * Schema: gogglesCrashes/{variant}/{version}/{crashSha} — the variant is the
 * numeric HardwareModel value as a string ("4" = MAGNETIC_25, "5" = DISPLAY_V2_26).
 * @param {Function} addError - Optional error handler from ErrorContext
 */
export const fetchGogglesCrashVariants = async (addError = null) => {
  try {
    const idToken = await getIdToken();
    if (!idToken) throw new Error('Not authenticated. Please log in.');

    const response = await fetch(buildApiUrl('/goggles-crashes'), {
      method: 'GET',
      headers: getAuthHeaders(idToken)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(errorData.message || errorData.error || `Failed to fetch crash variants: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    if (addError) addError(error.message || 'Error fetching goggles crash variants', error.stack, 'Goggles Crashes');
    console.error('Error fetching goggles crash variants:', error);
    throw error;
  }
};

/**
 * Fetches all software versions that have crashes for a hardware variant.
 * @param {string} variant - Hardware variant (numeric string, e.g. "4")
 * @param {Function} addError - Optional error handler from ErrorContext
 */
export const fetchVariantVersions = async (variant, addError = null) => {
  try {
    const idToken = await getIdToken();
    if (!idToken) throw new Error('Not authenticated. Please log in.');

    const response = await fetch(buildApiUrl(`/goggles-crashes/${encodeURIComponent(variant)}`), {
      method: 'GET',
      headers: getAuthHeaders(idToken)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(errorData.message || errorData.error || `Failed to fetch versions for variant ${variant}: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    if (addError) addError(error.message || 'Error fetching variant versions', error.stack, 'Goggles Crashes');
    console.error('Error fetching variant versions:', error);
    throw error;
  }
};

/**
 * Fetches all unique crashes for a specific hardware variant + software version.
 * @param {string} variant - Hardware variant (numeric string, e.g. "4")
 * @param {string} version - Software version (e.g., "1.2.1.12")
 * @param {Function} addError - Optional error handler from ErrorContext
 */
export const fetchVersionCrashes = async (variant, version, addError = null) => {
  try {
    const idToken = await getIdToken();
    if (!idToken) throw new Error('Not authenticated. Please log in.');

    const response = await fetch(buildApiUrl(`/goggles-crashes/${encodeURIComponent(variant)}/${encodeURIComponent(version)}`), {
      method: 'GET',
      headers: getAuthHeaders(idToken)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(errorData.message || errorData.error || `Failed to fetch crashes for version ${version}: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    if (addError) addError(error.message || 'Error fetching version crashes', error.stack, 'Goggles Crashes');
    console.error('Error fetching version crashes:', error);
    throw error;
  }
};

/**
 * Fetches full details for a specific crash.
 * @param {string} variant - Hardware variant (numeric string, e.g. "4")
 * @param {string} version - Software version
 * @param {string} crashSha - Crash SHA hash
 * @param {Function} addError - Optional error handler from ErrorContext
 */
export const fetchCrashDetails = async (variant, version, crashSha, addError = null) => {
  try {
    const idToken = await getIdToken();
    if (!idToken) throw new Error('Not authenticated. Please log in.');

    const response = await fetch(buildApiUrl(`/goggles-crashes/${encodeURIComponent(variant)}/${encodeURIComponent(version)}/${encodeURIComponent(crashSha)}`), {
      method: 'GET',
      headers: getAuthHeaders(idToken)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(errorData.message || errorData.error || `Failed to fetch crash details: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    if (addError) addError(error.message || 'Error fetching crash details', error.stack, 'Goggles Crashes');
    console.error('Error fetching crash details:', error);
    throw error;
  }
};
